import * as React from "react";
import { cn } from "./utils";

/** D-125: noun/status chip. Glass + 6px type-color dot. Never inverted (inverted is selected/primary). */
export type StatusPillTone =
  | "neutral"
  | "danger"
  | "warning"
  | "success"
  | "info"
  | "purple"
  | "pink"
  | "orange"
  | "cyan"
  | "violet";

const DOT: Record<StatusPillTone, string> = {
  neutral: "bg-slate-400 dark:bg-slate-500",
  danger: "bg-red-500",
  warning: "bg-amber-500",
  success: "bg-emerald-500",
  info: "bg-sky-500",
  purple: "bg-purple-500",
  pink: "bg-pink-500",
  orange: "bg-orange-500",
  cyan: "bg-cyan-500",
  violet: "bg-violet-500",
};

export const statusPillClass =
  "inline-flex items-center gap-1.5 h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wide border border-white/70 bg-white/70 text-slate-700 dark:border-white/15 dark:bg-transparent dark:text-slate-300";

export function sizeTone(size: string | null | undefined): StatusPillTone {
  switch (String(size || "").toUpperCase()) {
    case "XS":
      return "neutral";
    case "S":
      return "cyan";
    case "M":
      return "info";
    case "L":
      return "warning";
    case "XL":
      return "danger";
    default:
      return "neutral";
  }
}

export function ragTone(rag: string | null | undefined): StatusPillTone {
  if (rag === "red") return "danger";
  if (rag === "amber") return "warning";
  return "success";
}

export function StatusPill({
  tone = "neutral",
  className,
  children,
  title,
  ...rest
}: {
  tone?: StatusPillTone;
  className?: string;
  children: React.ReactNode;
  title?: string;
} & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span title={title} className={cn(statusPillClass, className)} {...rest}>
      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", DOT[tone])} aria-hidden />
      {children}
    </span>
  );
}

/** Count / numeric badge — muted glass, not a type pill. */
export const countBadgeClass =
  "inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums border border-white/70 bg-white/70 text-slate-600 dark:border-white/15 dark:bg-transparent dark:text-slate-400";

export function CountBadge({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <span className={cn(countBadgeClass, className)}>{children}</span>;
}
